import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { homepageAPI, homepageDataAPI, servicesAPI, testimonialAPI, productAPI, newsletterAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { getImageUrl } from '../utils/apiUtils';
import { 
  ArrowRight, 
  Code, 
  Smartphone, 
  TrendingUp, 
  Zap,
  Users,
  Award,
  CheckCircle,
  Star,
  Play,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Palette,
  BarChart3,
  ShoppingBag,
  CreditCard,
  Calendar,
  Headphones,
  Globe,
  ExternalLink,
  MessageCircle,
  Phone,
  Mail,
  HelpCircle,
  Settings,
  Database,
  Cloud,
  Shield,
  Heart,
  Target,
  Briefcase,
  DollarSign,
  MapPin,
  Check,
  Clock
} from 'lucide-react';
import { gsap } from 'gsap';
import { motion } from 'framer-motion';
import { useNotificationHook } from '../hooks/useNotificationHook';
import SplineComponent from '../components/Spline/SplineComponent';

const Home = () => {
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const [homeData, setHomeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [testimonialsError, setTestimonialsError] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [statsAnimated, setStatsAnimated] = useState(false);
  const slideInterval = useRef(null);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [featuredProductsLoading, setFeaturedProductsLoading] = useState(false);
  const [featuredProductsError, setFeaturedProductsError] = useState(null);

  const [bannerImageLoading, setBannerImageLoading] = useState(false);
  const [aboutImageLoading, setAboutImageLoading] = useState(false);
  const [pricingToggle, setPricingToggle] = useState(false); // false = monthly, true = yearly

  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState('');

  const { notify } = useNotificationHook();

  // Carousel functions
  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const previousSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  // Auto-slide effect
  useEffect(() => {
    if (testimonials.length > 0) {
      slideInterval.current = setInterval(() => {
        nextSlide();
      }, 5000); // Change slide every 5 seconds

      return () => clearInterval(slideInterval.current);
    }
  }, [testimonials.length]);

  // Fetch homepage data from API
  const fetchHomepageData = async () => {
    try {
      setLoading(true);
      const [homepageResponse, homepageDataResponse] = await Promise.all([
        homepageAPI.getContent(),
        homepageDataAPI.getContent()
      ]);
      
      if (homepageResponse?.data?.success && homepageDataResponse?.data?.success) {
        const homepageData = homepageResponse.data.data;
        const newsletterSupportData = homepageDataResponse.data.data;
        

        
        // Ensure proper parsing of JSON fields
        const processedData = {
          ...homepageData,
          ...newsletterSupportData, // Merge Newsletter and Support data
          hero_stats: (() => {
            try {
              return typeof homepageData.hero_stats === 'string' ? JSON.parse(homepageData.hero_stats) : (homepageData.hero_stats || []);
            } catch (error) {
              console.error('Error parsing hero_stats:', error);
              return [];
            }
          })(),
          about_features: (() => {
            try {
              return typeof homepageData.about_features === 'string' ? JSON.parse(homepageData.about_features) : (homepageData.about_features || []);
            } catch (error) {
              console.error('Error parsing about_features:', error);
              return [];
            }
          })(),
          pricing_plans: (() => {
            try {
              return typeof homepageData.pricing_plans === 'string' ? JSON.parse(homepageData.pricing_plans) : (homepageData.pricing_plans || []);
            } catch (error) {
              console.error('Error parsing pricing_plans:', error);
              return [];
            }
          })(),
          brand_logos: (() => {
            try {
              return typeof homepageData.brand_logos === 'string' ? JSON.parse(homepageData.brand_logos) : (homepageData.brand_logos || []);
            } catch (error) {
              console.error('Error parsing brand_logos:', error);
              return [];
            }
          })(),
          services_left_card_points: (() => {
            try {
              return typeof homepageData.services_left_card_points === 'string' ? JSON.parse(homepageData.services_left_card_points) : (homepageData.services_left_card_points || []);
            } catch (error) {
              console.error('Error parsing services_left_card_points:', error);
              return [];
            }
          })(),
          why_choose_us_items: (() => {
            try {
              return typeof homepageData.why_choose_us_items === 'string' ? JSON.parse(homepageData.why_choose_us_items) : (homepageData.why_choose_us_items || []);
            } catch (error) {
              console.error('Error parsing why_choose_us_items:', error);
              return [];
            }
          })(),
          working_process_steps: (() => {
            try {
              return typeof homepageData.working_process_steps === 'string' ? JSON.parse(homepageData.working_process_steps) : (homepageData.working_process_steps || []);
            } catch (error) {
              console.error('Error parsing working_process_steps:', error);
              return [];
            }
          })(),

          support_extra_buttons: (() => {
            try {
              return typeof newsletterSupportData.support_extra_buttons === 'string' ? JSON.parse(newsletterSupportData.support_extra_buttons) : (newsletterSupportData.support_extra_buttons || []);
            } catch (error) {
              console.error('Error parsing support_extra_buttons:', error);
              return [];
            }
          })(),
          team_members: (() => {
            try {
              return typeof newsletterSupportData.team_members === 'string' ? JSON.parse(newsletterSupportData.team_members) : (newsletterSupportData.team_members || []);
            } catch (error) {
              console.error('Error parsing team_members from homepageData:', error);
              return [];
            }
          })(),
          payment_methods_logos: (() => {
            try {
              return typeof newsletterSupportData.payment_methods_logos === 'string' ? JSON.parse(newsletterSupportData.payment_methods_logos) : (newsletterSupportData.payment_methods_logos || []);
            } catch (error) {
              console.error('Error parsing payment_methods_logos:', error);
              return [];
            }
          })(),
          pricing_plans: (() => {
            try {
              return typeof newsletterSupportData.pricing_plans === 'string' ? JSON.parse(newsletterSupportData.pricing_plans) : (newsletterSupportData.pricing_plans || []);
            } catch (error) {
              console.error('Error parsing pricing_plans from homepageData:', error);
              return [];
            }
          })()
        };
        
        setHomeData(processedData);
        setError(null);
      } else {
        console.error('Failed to fetch homepage data:', homepageResponse, homepageDataResponse);
        setError('Failed to load homepage data');
        notify.error('Failed to load homepage data', 'Loading Error');
      }
    } catch (err) {
      console.error('Error fetching homepage data:', err);
      setError('Error loading homepage data');
      notify.error('Error loading homepage data', 'Network Error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch services from API
  const fetchServices = async () => {
    try {
      setServicesLoading(true);
      setServicesError(null);
      
      const response = await servicesAPI.getAll({
        status: 'active',
        featured: 'true',
        limit: 4
      });
      
      if (response?.data?.success) {
        const servicesData = response.data.data.services || [];
        
        // Parse JSON fields and format data
        const formattedServices = servicesData.map(service => {
          try {
            return {
              ...service,
              features: typeof service.features === 'string' ? JSON.parse(service.features) : service.features || [],
              featured: Boolean(service.featured),
              // Map icon names to components
              icon: getIconComponent(service.icon)
            };
          } catch (parseError) {
            console.error('Error parsing service:', service, parseError);
            return {
              ...service,
              features: [],
              featured: Boolean(service.featured),
              icon: Code
            };
          }
        });
        
        setServices(formattedServices);
      } else {
        console.error('API response not successful:', response);
        setServicesError('Failed to fetch services');
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setServicesError(`Error loading services: ${err.message}`);
    } finally {
      setServicesLoading(false);
    }
  };

  // Fetch featured products from API
  const fetchFeaturedProducts = async () => {
    try {
      setFeaturedProductsLoading(true);
      setFeaturedProductsError(null);
      
      const response = await productAPI.getAll({
        status: 'active',
        featured: 'true',
        limit: 3
      });
      
      if (response?.data?.success) {
        const productsData = response.data.data.products || [];
        
        // Parse JSON fields and format data
        const formattedProducts = productsData.map(product => {
          try {
            return {
              ...product,
              tags: typeof product.tags === 'string' ? JSON.parse(product.tags) : product.tags || [],
              gallery: typeof product.gallery === 'string' ? JSON.parse(product.gallery) : product.gallery || [],
              features: typeof product.features === 'string' ? JSON.parse(product.features) : product.features || [],
              featured: Boolean(product.featured),
              // Format price
              formattedPrice: `₹${parseInt(product.regular_license_price || product.price).toLocaleString()}`,
              formattedOriginalPrice: (product.regular_license_original_price || product.original_price) ? 
                `₹${parseInt(product.regular_license_original_price || product.original_price).toLocaleString()}` : null,
              // Calculate discount percentage
              discountPercentage: (product.regular_license_original_price || product.original_price) ? 
                Math.round(((product.regular_license_original_price || product.original_price) - (product.regular_license_price || product.price)) / (product.regular_license_original_price || product.original_price) * 100) : 0
            };
          } catch (parseError) {
            console.error('Error parsing product:', product, parseError);
            return {
              ...product,
              tags: [],
              gallery: [],
              features: [],
              featured: Boolean(product.featured),
              formattedPrice: `₹${parseInt(product.price).toLocaleString()}`,
              formattedOriginalPrice: null,
              discountPercentage: 0
            };
          }
        });
        
        setFeaturedProducts(formattedProducts);
      } else {
        console.error('API response not successful:', response);
        setFeaturedProductsError('Failed to fetch featured products');
      }
    } catch (err) {
      console.error('Error fetching featured products:', err);
      setFeaturedProductsError(`Error loading featured products: ${err.message}`);
    } finally {
      setFeaturedProductsLoading(false);
    }
  };

  // Fetch testimonials from API
  const fetchTestimonials = async () => {
    try {
      setTestimonialsLoading(true);
      setTestimonialsError(null);

      const response = await testimonialAPI.getAll({
        limit: 3
      });

      if (response?.data?.success) {
        const testimonialsData = response.data.data.testimonials || [];
        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🔍 Website fetched testimonials:', testimonialsData);
        testimonialsData.forEach((testimonial, index) => {
          console.log(`🔍 Testimonial ${index + 1}:`, {
            name: testimonial.name,
            image: testimonial.image,
            hasImage: !!testimonial.image
          });
        });
        setTestimonials(testimonialsData);
      } else {
        console.error('API response not successful for testimonials:', response);
        setTestimonialsError('Failed to fetch testimonials');
      }
    } catch (err) {
      console.error('Error fetching testimonials:', err);
      setTestimonialsError(`Error loading testimonials: ${err.message}`);
    } finally {
      setTestimonialsLoading(false);
    }
  };

  // Get icon component based on icon name
  const getIconComponent = (iconName) => {
    const icons = {
      'Code': Code,
      'Smartphone': Smartphone,
      'TrendingUp': TrendingUp,
      'Globe': Globe,
      'ShoppingCart': TrendingUp,
      'Palette': Palette,
      'Database': Database,
      'Cloud': Cloud,
      'Shield': Shield,
      'Zap': Zap,
      'Users': Users,
      'Settings': Settings,
      'Headphones': Headphones,
      'MessageCircle': MessageCircle,
      'Phone': Phone,
      'Mail': Mail,
      'HelpCircle': HelpCircle,
      'CheckCircle': CheckCircle,
      'ArrowRight': ArrowRight,
      'Heart': Heart,
      'Target': Target,
      'Briefcase': Briefcase,
      'CreditCard': CreditCard,
      'DollarSign': DollarSign,
      'MapPin': MapPin,
      'AlertCircle': AlertCircle,
      'Check': Check,
      'Clock': Clock
    };
    return icons[iconName] || CheckCircle;
  };

  useEffect(() => {
    fetchHomepageData();
    fetchServices();
    fetchFeaturedProducts();
    fetchTestimonials();
    // fetchLatestProjects(); // Removed - Projects section hidden from public website
  }, []);

  // Debug banner data
  useEffect(() => {
    if (homeData) {
      if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🔍 Hero Banner Data:', {
        banner_type: homeData.hero_banner_type,
        banner_image: homeData.hero_banner_image,
        banner_url: homeData.hero_banner_url,
        banner_title: homeData.hero_banner_title
      });
      
      if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('🔍 About Us Data:', {
        about_image: homeData.about_image,
        about_title: homeData.about_title,
        about_subtitle: homeData.about_subtitle,
        about_description: homeData.about_description?.substring(0, 50) + '...'
      });
      
      // Check if about_image is different from default
              if (homeData.about_image && homeData.about_image !== '/images/about-main.svg') {
        console.log('✅ About image URL detected:', homeData.about_image);
      } else {
        if (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1') console.log('⚠️ About image using default or empty');
      }
    }
  }, [homeData]);

  // Reset animation state when homeData changes
  useEffect(() => {
    setStatsAnimated(false);
  }, [homeData]);

  useEffect(() => {
    // Wait for data to be loaded and elements to be rendered
    if (!homeData || loading) return;

    // Check if elements exist before running GSAP animations
    const heroElements = {
      title: document.querySelector('.hero-title'),
      subtitle: document.querySelector('.hero-subtitle'),
      buttons: document.querySelector('.hero-buttons'),
      trust: document.querySelector('.hero-trust'),
      badge: document.querySelector('.hero-badge'),
      image: document.querySelector('.hero-image')
    };

    // Only run animations if elements exist
    const hasElements = Object.values(heroElements).some(el => el !== null);
    
    if (!hasElements) {
      console.log('⚠️ Hero elements not found, skipping GSAP animations');
      return;
    }

    // Set initial states to prevent flash - only for existing elements
    if (heroElements.title) gsap.set('.hero-title', { opacity: 0, y: 50 });
    if (heroElements.subtitle) gsap.set('.hero-subtitle', { opacity: 0, y: 30 });
    if (heroElements.buttons) gsap.set('.hero-buttons', { opacity: 0, y: 30 });
    if (heroElements.trust) gsap.set('.hero-trust', { opacity: 0, y: 20 });
    if (heroElements.badge) gsap.set('.hero-badge', { opacity: 0, scale: 0.8 });
    if (heroElements.image) gsap.set('.hero-image', { opacity: 0, scale: 0.9 });

    // Hero animation with proper timing - only animate existing elements
    const tl = gsap.timeline({ delay: 0.2 });
    
    if (heroElements.badge) {
      tl.to('.hero-badge', { duration: 0.6, opacity: 1, scale: 1, ease: 'back.out(1.7)' });
    }
    if (heroElements.title) {
      tl.to('.hero-title', { duration: 0.8, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.3');
    }
    if (heroElements.subtitle) {
      tl.to('.hero-subtitle', { duration: 0.8, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.5');
    }
    if (heroElements.buttons) {
      tl.to('.hero-buttons', { duration: 0.8, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.5');
    }
    if (heroElements.trust) {
      tl.to('.hero-trust', { duration: 0.6, opacity: 1, y: 0, ease: 'power3.out' }, '-=0.4');
    }
    if (heroElements.image) {
      tl.to('.hero-image', { duration: 1, opacity: 1, scale: 1, ease: 'power3.out' }, '-=0.8');
    }

    // Slow stats counter animation
    const statsObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !statsAnimated) {
          // Only animate if we have actual data and haven't animated yet
          if (homeData?.hero_stats && homeData.hero_stats.length > 0) {
            setStatsAnimated(true); // Mark as animated to prevent re-triggering
            
            const statNumbers = entry.target.querySelectorAll('.stat-number');
            statNumbers.forEach((stat, index) => {
              const finalValue = parseInt(stat.textContent) || 0;
              if (finalValue > 0) {
                // Set initial value to 0
                stat.textContent = '0';
                
                gsap.to(stat, {
                  duration: 4, // Slow animation - 4 seconds
                  textContent: finalValue,
                  ease: 'power2.out',
                  snap: { textContent: 1 },
                  delay: index * 0.5, // Longer delay between each stat
                  onUpdate: function() {
                    // Ensure the text content is properly updated
                    stat.textContent = Math.floor(this.targets()[0].textContent);
                  }
                });
              }
            });
          }
        }
      });
    }, { threshold: 0.5 });

    if (statsRef.current) {
      statsObserver.observe(statsRef.current);
    }

    return () => {
      // Cleanup animations
      tl.kill();
      if (statsRef.current) {
        statsObserver.unobserve(statsRef.current);
      }
      setStatsAnimated(false);
    };
  }, [homeData, loading]); // Run when homeData or loading changes

  // Auto-slide effect
  useEffect(() => {
    if (testimonials.length > 0) {
      slideInterval.current = setInterval(() => {
        nextSlide();
      }, 5000); // Change slide every 5 seconds

      return () => clearInterval(slideInterval.current);
    }
  }, [testimonials.length]);



  // Show loading state when data is being fetched
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading homepage...</p>
        </div>
      </div>
    );
  }

  // Show error state when there's an error
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Unable to Load Content</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">We're experiencing technical difficulties. Please try again later.</p>
          <button 
            onClick={() => {
              setError(null);
              fetchHomepageData();
            }}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Don't render content if homeData is null (API failed or not loaded)
  if (!homeData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading content...</p>
        </div>
      </div>
    );
  }


  const handleNewsletterSubscribe = async (e) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;

    setNewsletterLoading(true);
    try {
      const response = await newsletterAPI.subscribe({ email: newsletterEmail });
      if (response.data.success) {
        setNewsletterSubscribed(true);
        setNewsletterEmail('');
        setNewsletterMessage('Successfully subscribed!');
      } else {
        setNewsletterMessage(response.data.message || 'Already subscribed');
      }
    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      setNewsletterMessage('Sorry, there was an error. Please try again.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Only render content if homeData exists and API call was successful */}
      {homeData && (
        <>
          {/* Hero Section */}
          <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
            {/* Enhanced Background with Better Colors */}
        <div className="absolute inset-0">
              {/* Primary gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/30 to-pink-900/20"></div>
              
              {/* Subtle mesh gradient */}
              <div className="absolute inset-0 bg-gradient-to-tr from-cyan-900/10 via-transparent to-indigo-900/10"></div>
              
              {/* Animated background shapes - reduced opacity and better positioning */}
              <div className="absolute top-10 left-10 w-64 h-64 bg-gradient-to-r from-blue-400/20 to-purple-400/20 dark:from-blue-500/10 dark:to-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute bottom-10 right-10 w-80 h-80 bg-gradient-to-r from-purple-400/20 to-pink-400/20 dark:from-purple-500/10 dark:to-pink-500/10 rounded-full blur-3xl animate-pulse animation-delay-2000"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/15 to-blue-400/15 dark:from-cyan-500/5 dark:to-blue-500/5 rounded-full blur-3xl animate-pulse animation-delay-4000"></div>
              
              {/* Subtle floating elements - smaller and more refined */}
              <div className="absolute top-20 left-20 w-4 h-4 bg-blue-500/40 dark:bg-blue-400/30 rounded-full floating-3d"></div>
              <div className="absolute top-40 right-32 w-6 h-6 bg-purple-500/40 dark:bg-purple-400/30 rounded-full floating-3d animation-delay-2000"></div>
              <div className="absolute bottom-32 left-1/4 w-3 h-3 bg-cyan-500/40 dark:bg-cyan-400/30 rounded-full floating-3d animation-delay-1000"></div>
              <div className="absolute bottom-20 right-20 w-5 h-5 bg-pink-500/40 dark:bg-pink-400/30 rounded-full floating-3d animation-delay-4000"></div>
              
              {/* Grid pattern overlay */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                  backgroundImage: `
                    linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)
                  `,
                  backgroundSize: '50px 50px'
                }}></div>
              </div>
        </div>

            <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative py-10 z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto pb-12 md:pb-0">
                <div className="text-center lg:text-left space-y-4 md:space-y-8 order-1 lg:order-1">
                  {/* Modern 3D Badge */}
              {homeData.hero_subtitle && (
                    <div className="hero-badge inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-3 text-sm font-semibold text-gray-900 dark:text-white shadow-lg transform hover:scale-105 transition-all duration-300">
                      <Zap className="w-4 h-4 text-blue-400" />
                  <span>{homeData.hero_subtitle}</span>
                </div>
              )}
              
                  {/* Modern 3D Title with Gradient */}
                  <h1 className="hero-title text-5xl md:text-5xl lg:text-6xl xl:text-7xl font-bold leading-[1.2]">
                    {homeData.hero_title_1 && <span className="text-gray-900 dark:text-white block">{homeData.hero_title_1}</span>}
                    {homeData.hero_title_2 && <span className="liquid-gradient-text block">{homeData.hero_title_2}</span>}
                    {homeData.hero_title_3 && <span className="text-2xl md:text-2xl lg:text-3xl xl:text-4xl font-normal text-gray-600 dark:text-gray-300 block mt-2">{homeData.hero_title_3}</span>}
              </h1>
              
              {/* Enhanced Subtitle */}
              {homeData.hero_description && (
                    <p className="hero-subtitle text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl mx-auto lg:mx-0 mt-8 md:mt-6">
                  {homeData.hero_description}
                </p>
              )}
              
                  {/* Modern 3D Buttons */}
              {(homeData.hero_primary_button || homeData.hero_secondary_button) && (
                    <div className="hero-buttons flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  {homeData.hero_primary_button && (
                        <Link to={homeData.hero_primary_link || '/contact'} className="group relative overflow-hidden bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold text-lg px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 shadow-lg">
                      <span className="relative z-10">{homeData.hero_primary_button}</span>
                      <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform duration-300" />
                    </Link>
                  )}
                  {homeData.hero_secondary_button && (
                        <Link to={homeData.hero_secondary_link || '/services'} className="group bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 text-gray-900 dark:text-white font-semibold text-lg px-8 py-4 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3">
                      <Play className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                      <span>{homeData.hero_secondary_button}</span>
                    </Link>
                  )}
                </div>
              )}
              
              {/* Trust Indicators */}
              {(homeData.hero_trust_clients || homeData.hero_trust_experience) && (
                    <div className="hero-trust flex items-center justify-center lg:justify-start gap-8 text-sm text-gray-600 dark:text-gray-400">
                  {homeData.hero_trust_clients && (
                    <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-blue-400" />
                      <span>{homeData.hero_trust_clients}</span>
                    </div>
                  )}
                  {homeData.hero_trust_experience && (
                    <div className="flex items-center gap-2">
                          <Award className="w-4 h-4 text-purple-400" />
                      <span>{homeData.hero_trust_experience}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
                                  {/* Modern 3D Image Section */}
                  <div className="hero-image relative flex justify-center lg:justify-end mt-2 md:mt-0 order-2 lg:order-2">
                    <div className="relative z-10 max-w-4xl w-full">
                      {/* Spline or Banner Image Display Logic */}
                      {homeData?.hero_spline_active && (homeData?.hero_spline_url || homeData?.hero_spline_code) ? (
                        <SplineComponent
                          splineUrl={homeData.hero_spline_url}
                          splineCode={homeData.hero_spline_code}
                          title={homeData.hero_spline_title}
                          description={homeData.hero_spline_description}
                          height="600px"
                          className="w-full"
                        />
                    ) : (
                      /* Main Image Container with Refined 3D Effect */
                <div className="relative transform hover:scale-105 transition-transform duration-500">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl opacity-30 transform rotate-3 scale-105"></div>
                        <div className="relative bg-white/90 backdrop-blur-md rounded-2xl p-4 shadow-xl border border-white/30">
                    {homeData?.hero_banner_type === 'image' && homeData?.hero_banner_image ? (
                      <div className="rounded-xl h-80 overflow-hidden relative">
                        {bannerImageLoading && (
                          <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                          </div>
                        )}
                        <img 
                          src={homeData.hero_banner_image} 
                          alt={homeData?.hero_banner_title || 'Hero Banner'} 
                          className="w-full h-full object-cover"
                          onLoadStart={() => setBannerImageLoading(true)}
                          onLoad={() => {
                            console.log('✅ Banner image loaded successfully');
                            setBannerImageLoading(false);
                          }}
                          onError={(e) => {
                            console.error('❌ Banner image failed to load:', homeData.hero_banner_image);
                            setBannerImageLoading(false);
                            // Fallback to default content if image fails to load
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center hidden">
                          <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                              <Code className="w-10 h-10 text-white" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">{homeData?.hero_banner_title || 'Digital Solutions'}</h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                              {homeData?.hero_banner_point_1 || 'Innovative'} • {homeData?.hero_banner_point_2 || 'Scalable'} • {homeData?.hero_banner_point_3 || 'Secure'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : homeData?.hero_banner_type === 'url' && homeData?.hero_banner_url ? (
                      <div className="rounded-xl h-80 overflow-hidden">
                        <iframe 
                          src={homeData.hero_banner_url} 
                          title={homeData?.hero_banner_title || 'Hero Banner'}
                          className="w-full h-full border-0"
                          onError={(e) => {
                            // Fallback to default content if iframe fails to load
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 flex items-center justify-center hidden">
                          <div className="text-center">
                            <div className="w-20 h-20 bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                              <Code className="w-10 h-10 text-white" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">{homeData?.hero_banner_title || 'Digital Solutions'}</h3>
                            <p className="text-gray-600 dark:text-gray-400 text-sm">
                              {homeData?.hero_banner_point_1 || 'Innovative'} • {homeData?.hero_banner_point_2 || 'Scalable'} • {homeData?.hero_banner_point_3 || 'Secure'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-xl h-80 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-20 h-20 bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <Code className="w-10 h-10 text-white" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-2">{homeData?.hero_banner_title || 'Digital Solutions'}</h3>
                          <p className="text-gray-600 dark:text-gray-400 text-sm">
                            {homeData?.hero_banner_point_1 || 'Innovative'} • {homeData?.hero_banner_point_2 || 'Scalable'} • {homeData?.hero_banner_point_3 || 'Secure'}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                    )}
                </div>
                </div>
                </div>
                
              {/* Refined Floating Icons */}
              <div className="absolute -bottom-2 -left-2 w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center floating-3d shadow-lg border border-white/30">
                <Code className="w-6 h-6 text-white" />
                </div>
              <div className="absolute -top-2 -right-2 w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center floating-3d animation-delay-1000 shadow-lg border border-white/30">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div className="absolute top-1/2 -left-2 w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center floating-3d animation-delay-2000 shadow-lg border border-white/30">
                <Smartphone className="w-4 h-4 text-white" />
            </div>
              <div className="absolute bottom-1/4 -right-2 w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center floating-3d animation-delay-4000 shadow-lg border border-white/30">
                <TrendingUp className="w-4 h-4 text-white" />
          </div>
        </div>
      </section>

          {/* Modern 3D Stats Section */}
          <section ref={statsRef} className="py-20 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 opacity-20"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {homeData?.hero_stats && homeData.hero_stats.length > 0 ? 
              homeData.hero_stats.map((stat, index) => {
                // Parse the value to extract number and suffix
                const value = stat.value.toString();
                const number = parseInt(value.replace(/[^0-9]/g, '')) || 0;
                const suffix = value.replace(/[0-9]/g, '') || '+';
                return (
                      <div key={index} className="text-center card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 dark:hover:bg-white/20 hover:bg-white/90 transition-all duration-500">
                        <div className="text-4xl md:text-5xl font-black gradient-text mb-3">
                      <span className="stat-number">{number}</span>{suffix}
                    </div>
                        <div className="text-gray-700 dark:text-white/90 font-semibold text-lg">
                      {stat.label}
                    </div>
                  </div>
                );
              }) : [
                { number: 500, label: 'Projects Completed', suffix: '+' },
                { number: 200, label: 'Happy Clients', suffix: '+' },
                { number: 5, label: 'Years Experience', suffix: '+' },
                { number: 50, label: 'Team Members', suffix: '+' }
              ].map((stat, index) => (
                    <div key={index} className="text-center card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 dark:hover:bg-white/20 hover:bg-white/90 transition-all duration-500">
                      <div className="text-4xl md:text-5xl font-black gradient-text mb-3">
                    <span className="stat-number">{stat.number}</span>{stat.suffix}
                  </div>
                      <div className="text-gray-700 dark:text-white/90 font-semibold text-lg">
                    {stat.label}
                  </div>
                </div>
              ))
            }
          </div>
        </div>
      </section>

          {/* Modern Brand Logos Section */}
          <section className="py-20 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 dark:from-indigo-900 dark:via-purple-900 dark:to-pink-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-r from-indigo-800 via-purple-800 to-pink-800 opacity-30"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {homeData?.brand_logos && homeData.brand_logos.length > 0 && (
            <div className="relative overflow-hidden">
              <div
                    className="flex items-center gap-16 animate-brand-logos-scroll"
                style={{
                  animation: `brand-logos-scroll ${homeData.brand_logos.length * 2.5}s linear infinite` // speed based on logo count
                }}
              >
                {/* Duplicate logos for seamless scroll */}
                {[...homeData.brand_logos, ...homeData.brand_logos].map((logo, idx) => (
                  <a
                    key={idx}
                    href={logo.link || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                        className="block flex-shrink-0 w-40 h-24 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl shadow-glow mx-6 flex items-center justify-center transition-all duration-500 hover:scale-125 hover:shadow-glow-lg border border-white/20"
                  >
                    {logo.image ? (
                      <img
                        src={logo.image}
                        alt="Brand Logo"
                            className="w-full h-full object-contain drop-shadow-lg"
                        draggable="false"
                      />
                    ) : logo.link ? (
                      <div className="w-full h-full flex items-center justify-center">
                            <div className="text-white text-sm font-semibold text-center px-3">
                          {typeof logo.link === 'string' && logo.link.includes('//') ? 
                            new URL(logo.link).hostname.replace('www.', '') : 
                            (typeof logo.link === 'string' ? logo.link.replace(/^https?:\/\//, '').replace('www.', '') : 'Brand')
                          }
                        </div>
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                            <div className="text-white text-sm font-semibold text-center px-3">
                          Brand Logo
                        </div>
                      </div>
                    )}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

          {/* Modern 3D About Us Section */}
          <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-purple-800 to-slate-800 opacity-20"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="max-w-7xl mx-auto relative z-10">
              <div className="flex flex-col lg:flex-row gap-16 items-center">
                {/* Left Side - Large Image with 3D Badge */}
            <div className="lg:w-1/2 relative">
                  {/* Main Image with 3D Effect */}
              <div className="relative">
                    <div className="w-full h-96 card-3d bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 rounded-3xl overflow-hidden shadow-3d relative">

                  
                  {aboutImageLoading && (
                    <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 flex items-center justify-center z-10">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  )}
                  
                  <img 
                    src={homeData?.about_image || "/images/about-main.svg"} 
                    alt="People working together in modern office" 
                    className="w-full h-full object-cover"
                    onLoadStart={() => {
                      console.log('🔄 About image loading started:', homeData?.about_image);
                      setAboutImageLoading(true);
                    }}
                    onLoad={() => {
                      console.log('✅ About image loaded successfully:', homeData?.about_image);
                      setAboutImageLoading(false);
                    }}
                    onError={(e) => {
                      console.error('❌ About image failed to load:', homeData?.about_image);
                      setAboutImageLoading(false);
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center hidden">
                    <div className="text-white text-center">
                      <Users className="w-24 h-24 mb-4" />
                      <p className="text-lg font-semibold">Team Collaboration</p>
                      <p className="text-sm opacity-90">Modern workspace</p>
                    </div>
                  </div>
                </div>
                
                    {/* Modern 3D Experience Badge - Top Left */}
                    <div className="absolute -top-8 -left-8 w-32 h-32 md:w-36 md:h-36 card-3d bg-gradient-to-br from-pink-500 via-purple-500 to-indigo-500 rounded-full flex items-center justify-center shadow-glow-lg border-4 border-white/30 floating-3d">
                  <div className="text-center text-white">
                        <div className="text-lg font-black leading-tight">{homeData?.about_experience_years || "25+"}</div>
                        <div className="text-sm font-bold leading-tight">Years</div>
                        <div className="text-sm font-bold leading-tight">Experience</div>
                  </div>
                </div>
              </div>
            </div>
            
                {/* Right Side - Modern 3D Content */}
                <div className="lg:w-1/2 space-y-8">
                  {/* Modern Subheading */}
              <div className="inline-block">
                    <span className="text-lg md:text-xl font-bold tracking-wide gradient-text">
                  {homeData?.about_subtitle || "About Us"}
                </span>
              </div>
              
                  {/* Modern 3D Heading */}
                  <h2 className="text-4xl lg:text-6xl font-black gradient-text leading-tight">
                {homeData?.about_title || "Best Services We Provide"}
              </h2>
              
                  {/* Modern Description */}
                  <p className="text-lg md:text-xl text-white/90 max-w-lg leading-relaxed">
                {homeData?.about_description || "Every month we pick some best products for you. This month's best web themes & templates have arrived, chosen by our content specialists."}
              </p>
              
                  {/* Modern 3D Bullet Points */}
                  <div className="space-y-6">
                {Array.isArray(homeData?.about_features) && homeData.about_features.map((feature, index) => {
                  const IconComponent = feature.icon ? getIconComponent(feature.icon) : CheckCircle;
                  return (
                        <div key={index} className="flex items-start space-x-4 card-3d bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-500">
                          <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-glow">
                            <IconComponent className="w-4 h-4 text-white" />
                      </div>
                      <div>
                            <h3 className="text-white font-bold text-lg">{feature.title}</h3>
                            <p className="text-white/80 text-base mt-2">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  );
                }) || (
                  <>
                        <div className="flex items-start space-x-4 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-500">
                          <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-glow">
                            <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div>
                            <h3 className="text-white font-bold text-lg">High Quality Services</h3>
                            <p className="text-white/80 text-base mt-2">
                          Lorem ipsum dolor sit amet consectetur. Lobortis aliquet tincidunt donec turpis nibh nunc dolor feugiat tellus.
                        </p>
                      </div>
                    </div>
                    
                        <div className="flex items-start space-x-4 card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-500">
                          <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-glow">
                            <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div>
                            <h3 className="text-white font-bold text-lg">Top Tier Business Acquisition</h3>
                            <p className="text-white/80 text-base mt-2">
                          Lorem ipsum dolor sit amet consectetur. Lobortis aliquet tincidunt donec turpis nibh nunc dolor feugiat tellus.
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
                  {/* Modern 3D CTA Button */}
                  <div className="pt-6">
                <Link to={homeData?.about_button_link || "/about"}>
                      <button className="card-3d bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-pink-600 hover:via-purple-600 hover:to-indigo-600 hover:scale-110 transition-all duration-500 shadow-glow">
                    {homeData?.about_button_text || "More About Us"}
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

          {/* Modern Services Section */}
          <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-purple-100/20 to-pink-100/20 dark:from-blue-800/10 dark:via-purple-800/10 dark:to-pink-800/10"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
              <div className="text-center mb-20">
                <div className="inline-block bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-full text-sm font-bold mb-6 shadow-lg">
              {homeData?.services_subtitle || "Our Services"}
            </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8">
              {homeData?.services_title || "We Do Everything On Product Design"}
            </h2>
                <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {homeData?.services_description || "We provide comprehensive product design services that help businesses create exceptional user experiences and drive growth."}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Large Card - Strategy */}
                <div className="lg:col-span-1 card-3d bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-600 rounded-3xl shadow-3d p-8 text-white transform hover:scale-105 transition-all duration-500">
                  <h3 className="text-3xl font-bold mb-8">{homeData?.services_left_card_title || "Strategy"}</h3>
                  <ul className="space-y-6">
                {Array.isArray(homeData?.services_left_card_points) && homeData.services_left_card_points.map((point, index) => (
                      <li key={index} className="flex items-center space-x-4">
                        <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-white" />
                        </div>
                        <span className="text-lg font-medium">{point}</span>
                  </li>
                )) || (
                  <>
                        <li className="flex items-center space-x-4">
                          <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-lg font-medium">Market Analysis</span>
                    </li>
                        <li className="flex items-center space-x-4">
                          <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-lg font-medium">Branding</span>
                    </li>
                        <li className="flex items-center space-x-4">
                          <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-lg font-medium">Design Sprint</span>
                    </li>
                        <li className="flex items-center space-x-4">
                          <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-lg font-medium">User Research</span>
                    </li>
                        <li className="flex items-center space-x-4">
                          <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                          <span className="text-lg font-medium">Competitive Analysis</span>
                    </li>
                  </>
                )}
              </ul>
            </div>

            {/* Right Grid - Services from Admin Panel */}
            <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-6">
              {services && services.length > 0 ? (
                services.slice(0, 6).map((service, index) => {
                  const IconComponent = service.icon ? getIconComponent(service.icon) : TrendingUp;
                  return (
                        <div key={service.id || index} className="card-3d bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-2xl shadow-lg p-6 hover:scale-105 transition-all duration-500 border border-white/20 dark:border-slate-700/50">
                          <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                            <IconComponent className="w-7 h-7 text-white" />
                      </div>
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                        {service.title}
                      </h3>
                          <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  );
                })
              ) : (
                // Fallback services if no services from API
                [
                  {
                    icon: TrendingUp,
                    title: "Strategy",
                    description: "Comprehensive market analysis and strategic planning for your product success."
                  },
                  {
                    icon: Palette,
                    title: "Design",
                    description: "Beautiful and functional designs that create exceptional user experiences."
                  },
                  {
                    icon: Code,
                    title: "Creative Tech",
                    description: "Innovative technical solutions that bring your ideas to life."
                  },
                  {
                    icon: Smartphone,
                    title: "Mobile Apps",
                    description: "Native and cross-platform mobile applications for modern businesses."
                  },
                  {
                    icon: BarChart3,
                    title: "Web Development",
                    description: "Responsive web applications built with cutting-edge technologies."
                  },
                  {
                    icon: ShoppingBag,
                    title: "Analytics",
                    description: "Data-driven insights to optimize your product performance."
                  }
                ].map((service, index) => (
                      <div key={index} className="card-3d bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-2xl shadow-lg p-6 hover:scale-105 transition-all duration-500 border border-white/20 dark:border-slate-700/50">
                        <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                          <service.icon className="w-7 h-7 text-white" />
                    </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                      {service.title}
                    </h3>
                        <p className="text-gray-600 dark:text-gray-300 text-base leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

          {/* Modern Featured Products Section */}
          <section className="py-24 bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
            </div>
            
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
              <div className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              {homeData?.featured_products_title || "Featured Products"}
            </h2>
                <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {homeData?.featured_products_description || "Discover our handpicked selection of premium products designed to enhance your digital experience."}
            </p>
          </div>

          {featuredProductsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {[...Array(3)].map((_, index) => (
                <div key={index} className="bg-white dark:bg-slate-800 rounded-xl shadow-lg overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-200 dark:bg-gray-700"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded mb-4"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : featuredProductsError ? (
            <div className="text-center py-12">
              <p className="text-red-500 dark:text-red-400">{featuredProductsError}</p>
            </div>
          ) : featuredProducts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {featuredProducts.map((product, index) => (
                    <div key={product.id || index} className="card-3d bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-3xl shadow-xl overflow-hidden hover:scale-105 transition-all duration-500 border border-white/20 dark:border-slate-700/50">
                      <div className="h-56 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-500 flex items-center justify-center relative">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="text-white text-center" style={{ display: product.image ? 'none' : 'flex' }}>
                          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                            <ShoppingBag className="w-10 h-10" />
                      </div>
                          <p className="text-base font-medium">Product Image</p>
                    </div>
                    {product.discountPercentage > 0 && (
                          <div className="absolute top-3 right-3 bg-red-500 text-white text-sm px-3 py-1 rounded-full font-bold shadow-lg">
                        -{product.discountPercentage}%
                      </div>
                    )}
                  </div>
                      <div className="p-8">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                      {product.name}
                    </h3>
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center space-x-3">
                            <span className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                          {product.formattedPrice}
                        </span>
                        {product.formattedOriginalPrice && (
                              <span className="text-lg text-gray-500 dark:text-gray-400 line-through">
                            {product.formattedOriginalPrice}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-1">
                        {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-5 h-5 ${i < (product.rating || 5) ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'}`} />
                        ))}
                      </div>
                    </div>
                        <div className="flex space-x-3">
                      <Link 
                        to={`/product/${product.slug}`}
                            className="flex-1 bg-gray-100 dark:bg-slate-700 text-gray-900 dark:text-white px-4 py-3 rounded-xl text-sm font-semibold hover:bg-gray-200 dark:hover:bg-slate-600 transition-all duration-300 text-center"
                      >
                        View Details
                      </Link>
                      <button 
                        onClick={() => {
                          addToCart(product);
                          // Show a success message or navigate to cart
                          navigate('/cart');
                        }}
                            className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-3 rounded-xl text-sm font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 text-center shadow-lg"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400">No featured products available</p>
            </div>
          )}

          <div className="text-center">
            <Link 
              to={homeData?.featured_products_button_link || "/products"}
                  className="card-3d bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl px-10 py-4 font-bold text-lg hover:from-blue-600 hover:to-purple-700 hover:scale-105 transition-all duration-500 inline-block shadow-glow"
            >
              {homeData?.featured_products_button_text || "View All Items"}
            </Link>
          </div>
        </div>
      </section>

          {/* Modern Why Choose Us Section */}
          <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800/20 via-purple-800/30 to-slate-800/20"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
              <div className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-8">
              {homeData?.why_choose_us_title || "Why Choose Us"}
            </h2>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {homeData?.why_choose_us_description || "We deliver exceptional results through our dedicated team and proven methodologies."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {Array.isArray(homeData?.why_choose_us_items) && homeData.why_choose_us_items.length > 0 ? (
              homeData.why_choose_us_items.map((item, index) => {
                const IconComponent = getIconComponent(item.icon);
                return (
                      <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 hover:scale-105 transition-all duration-500 border border-white/20">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-8 shadow-lg">
                          {IconComponent && <IconComponent className="w-10 h-10 text-white" />}
                    </div>
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                      {item.title}
                    </h3>
                        <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                );
              })
            ) : (
              // Fallback to default data if no API data
              [
                {
                  title: "Dedicated & Professional High Quality Team",
                  icon: "Users",
                  description: "Our experienced team of professionals is committed to delivering excellence in every project we undertake."
                },
                {
                  title: "Innovative Solutions & Modern Technologies",
                  icon: "Code",
                  description: "We leverage cutting-edge technologies and innovative approaches to solve complex business challenges."
                },
                {
                  title: "Proven Track Record & Client Satisfaction",
                  icon: "Award",
                  description: "With years of experience and countless successful projects, we ensure client satisfaction every time."
                }
              ].map((item, index) => {
                const IconComponent = getIconComponent(item.icon);
                return (
                      <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 hover:scale-105 transition-all duration-500 border border-white/20">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mb-8 shadow-lg">
                          {IconComponent && <IconComponent className="w-10 h-10 text-white" />}
                    </div>
                        <h3 className="text-2xl font-bold text-white mb-6">
                      {item.title}
                    </h3>
                        <p className="text-gray-300 text-lg leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

          {/* Modern Working Process Section */}
          <section className="py-24 bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100/10 via-purple-100/10 to-pink-100/10 dark:from-blue-800/5 dark:via-purple-800/5 dark:to-pink-800/5"></div>
            </div>
            
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
              <div className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8">
              {homeData?.working_process_title || "Our Working Process"}
            </h2>
                <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {homeData?.working_process_description || "We follow a systematic approach to ensure successful project delivery and client satisfaction."}
            </p>
          </div>

              <div className="card-3d bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-3xl shadow-3d px-12 py-16">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {Array.isArray(homeData?.working_process_steps) && homeData.working_process_steps.length > 0 ? (
                homeData.working_process_steps.map((process, index) => (
                  <div key={index} className="text-center text-white">
                        <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                          <span className="text-2xl font-bold">{process.step}</span>
                    </div>
                        <h3 className="text-xl font-bold mb-4">{process.title}</h3>
                        <p className="text-base text-white/90 leading-relaxed">{process.description}</p>
                    {index < homeData.working_process_steps.length - 1 && (
                          <div className="hidden md:block absolute top-1/2 transform -translate-y-1/2 right-0 text-white text-3xl">
                        →
                      </div>
                    )}
                  </div>
                ))
              ) : (
                // Fallback to default data if no API data
                [
                  {
                    step: "01",
                    title: "Understanding",
                    description: "We begin by thoroughly understanding your requirements, goals, and vision for the project."
                  },
                  {
                    step: "02",
                    title: "Ideation",
                    description: "Our team brainstorms creative solutions and develops innovative concepts for your project."
                  },
                  {
                    step: "03",
                    title: "Develop Idea",
                    description: "We transform concepts into tangible solutions using cutting-edge technologies and best practices."
                  },
                  {
                    step: "04",
                    title: "User Testing",
                    description: "Rigorous testing ensures your solution meets the highest standards of quality and performance."
                  }
                ].map((process, index) => (
                  <div key={index} className="text-center text-white">
                        <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                          <span className="text-2xl font-bold">{process.step}</span>
                    </div>
                        <h3 className="text-xl font-bold mb-4">{process.title}</h3>
                        <p className="text-base text-white/90 leading-relaxed">{process.description}</p>
                    {index < 3 && (
                          <div className="hidden md:block absolute top-1/2 transform -translate-y-1/2 right-0 text-white text-3xl">
                        →
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>



          {/* Modern Team Section */}
          <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800/20 via-purple-800/30 to-slate-800/20"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
              <div className="flex items-center justify-between mb-20">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              {homeData?.team_title || "Meet Our Team"}
            </h2>
            <Link 
              to={homeData?.team_button_link || "/team"}
                  className="text-blue-400 font-bold text-lg hover:text-blue-300 transition-colors duration-300"
            >
              {homeData?.team_button_text || "View All Members"}
            </Link>
          </div>

          <div className={`${Array.isArray(homeData?.team_members) && homeData.team_members.length === 1 
            ? 'grid grid-cols-1 gap-8 place-items-center' 
            : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8'}`}>
            {Array.isArray(homeData?.team_members) && homeData.team_members.length > 0 ? (
              homeData.team_members.map((member, index) => (
                    <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 text-center hover:scale-105 transition-all duration-500 border border-white/20">
                      <div className="w-80 h-80 sm:w-48 sm:h-48 md:w-56 md:h-56 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 overflow-hidden shadow-lg">
                    {member.image ? (
                      <img 
                        src={member.image} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                      />
                    ) : (
                          <Users className="w-40 h-40 sm:w-24 sm:h-24 md:w-28 md:h-28 text-white" />
                    )}
                  </div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {member.name}
                  </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-lg">
                    {member.role}
                  </p>
                </div>
              ))
            ) : (
              // Fallback to default data if no API data
              [
                {
                  name: "John Doe",
                  role: "Product Manager",
                  image: "/images/team-1.svg"
                },
                {
                  name: "Sarah Johnson",
                  role: "UI/UX Designer",
                  image: "/images/team-1.svg"
                },
                {
                  name: "Mike Chen",
                  role: "Frontend Developer",
                  image: "/images/team-1.svg"
                },
                {
                  name: "Emily Davis",
                  role: "Backend Developer",
                  image: "/images/team-1.svg"
                }
              ].map((member, index) => (
                    <div key={index} className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl p-8 text-center hover:scale-105 transition-all duration-500 border border-white/20">
                      <div className="w-48 h-48 sm:w-48 sm:h-48 md:w-56 md:h-56 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 overflow-hidden shadow-lg">
                    {member.image ? (
                      <img 
                        src={member.image} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full flex items-center justify-center" style={{ display: member.image ? 'none' : 'flex' }}>
                          <Users className="w-24 h-24 sm:w-24 sm:h-24 md:w-28 md:h-28 text-white" />
                    </div>
                  </div>
                      <h3 className="text-xl font-bold text-white mb-3">
                    {member.name}
                  </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-lg">
                    {member.role}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

          {/* Modern Payment Methods Section */}
          <section className="py-24 bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 dark:from-pink-900 dark:via-purple-900 dark:to-indigo-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-100/20 via-purple-100/20 to-indigo-100/20 dark:from-pink-800/10 dark:via-purple-800/10 dark:to-indigo-800/10"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Image */}
            <div className="relative">
                  <div className="w-full h-96 card-3d bg-gradient-to-br from-pink-400 via-purple-400 to-indigo-400 rounded-3xl overflow-hidden shadow-3d">
                {homeData?.payment_methods_image ? (
                  <img 
                    src={homeData.payment_methods_image} 
                    alt="Payment Methods"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-pink-400 to-purple-400 flex items-center justify-center">
                    <div className="text-white text-center">
                      <div className="w-32 h-32 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CreditCard className="w-16 h-16" />
                      </div>
                      <p className="text-lg font-semibold">Happy Customer</p>
                      <p className="text-sm opacity-90">With Payment Card</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Content */}
                <div className="space-y-10">
              <div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                  {homeData?.payment_methods_title || "Our Acceptable Payment Methods"}
                </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                  {homeData?.payment_methods_description || "We accept all major payment methods to make your shopping experience seamless and convenient. Choose from our wide range of secure payment options."}
                </p>
              </div>

              {/* CTA Button */}
              <Link 
                to={homeData?.payment_methods_button_link || "/payment-methods"}
                    className="card-3d bg-gradient-to-r from-gray-900 to-black dark:from-white dark:to-gray-100 text-white dark:text-gray-900 px-8 py-4 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-500 inline-block shadow-glow"
              >
                {homeData?.payment_methods_button_text || "Learn More About Payments"}
              </Link>

              {/* Payment Icons Grid */}
                  <div className="grid grid-cols-3 gap-6">
                {homeData?.payment_methods_logos && homeData.payment_methods_logos.length > 0 ? (
                  homeData.payment_methods_logos.map((logo, index) => (
                        <div key={index} className="card-3d bg-white/80 backdrop-blur-md rounded-2xl p-6 shadow-lg hover:scale-105 transition-all duration-500 border border-white/30">
                      {logo.image ? (
                            <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4 overflow-hidden shadow-md">
                          <img 
                            src={logo.image} 
                            alt={logo.name}
                                className="w-10 h-10 object-contain"
                            onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; }}
                          />
                        </div>
                      ) : (
                            <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                              <CreditCard className="w-8 h-8 text-gray-600" />
                        </div>
                      )}
                          <p className="text-sm text-center text-gray-700 font-semibold">{logo.name}</p>
                    </div>
                  ))
                ) : (
                  // Fallback to default data if no API data
                  [
                    { name: "PayPal", icon: CreditCard },
                    { name: "Bitcoin", icon: CreditCard },
                    { name: "Mastercard", icon: CreditCard },
                    { name: "VISA", icon: CreditCard },
                    { name: "Stripe", icon: CreditCard },
                    { name: "Google Pay", icon: CreditCard },
                    { name: "Apple Pay", icon: CreditCard },
                    { name: "Amazon Pay", icon: CreditCard },
                    { name: "Discover", icon: CreditCard }
                  ].map((method, index) => (
                        <div key={index} className="card-3d bg-white/80 backdrop-blur-md rounded-2xl p-6 shadow-lg hover:scale-105 transition-all duration-500 border border-white/30">
                          <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-md">
                            <method.icon className="w-8 h-8 text-gray-600" />
                      </div>
                          <p className="text-sm text-center text-gray-700 font-semibold">{method.name}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

          {/* Modern Testimonials Section */}
          <section className="py-24 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900 dark:via-indigo-900 dark:to-purple-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-indigo-100/20 to-purple-100/20 dark:from-blue-800/10 dark:via-indigo-800/10 dark:to-purple-800/10"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
              <div className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              {homeData?.testimonials_title || "Our Happy Client"}
            </h2>
                <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {homeData?.testimonials_description || "Hear what our satisfied customers have to say about their experience"}
            </p>
          </div>

          {testimonialsLoading ? (
            <div className="flex justify-center items-center py-16">
              <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
              <p className="ml-4 text-lg text-gray-600 dark:text-gray-400">Loading testimonials...</p>
            </div>
          ) : testimonialsError ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-red-600 dark:text-red-400">
              <AlertCircle className="w-16 h-16 mb-4" />
              <p className="text-lg">{testimonialsError}</p>
            </div>
          ) : testimonials.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              {/* Left Column - Client Image with Navigation */}
              <div className="relative">
                    <div className="w-80 h-80 card-3d bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-500 rounded-full flex items-center justify-center mx-auto shadow-3d overflow-hidden">
                  {testimonials[currentSlide]?.image ? (
                    <img 
                      src={`${getImageUrl(testimonials[currentSlide].image)}?t=${Date.now()}`}
                      alt={testimonials[currentSlide].name}
                      className="w-full h-full object-cover"
                      onLoad={() => {
                        console.log('🔍 Image loaded successfully:', testimonials[currentSlide].image);
                      }}
                      onError={(e) => {
                        console.log('🔍 Image failed to load:', testimonials[currentSlide].image);
                        console.log('🔍 Full image URL:', getImageUrl(testimonials[currentSlide].image));
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="w-full h-full flex items-center justify-center" style={{ display: testimonials[currentSlide]?.image ? 'none' : 'flex' }}>
                    <Users className="w-32 h-32 text-white" />
                  </div>
                </div>
                
                {/* Navigation Arrows */}
                <button 
                  onClick={previousSlide}
                      className="absolute left-6 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white/90 backdrop-blur-md rounded-full shadow-lg flex items-center justify-center hover:shadow-xl hover:scale-110 transition-all duration-300 border border-white/30"
                >
                      <ChevronLeft className="w-7 h-7 text-gray-600" />
                </button>
                <button 
                  onClick={nextSlide}
                      className="absolute right-6 top-1/2 transform -translate-y-1/2 w-14 h-14 bg-white/90 backdrop-blur-md rounded-full shadow-lg flex items-center justify-center hover:shadow-xl hover:scale-110 transition-all duration-300 border border-white/30"
                >
                      <ChevronRight className="w-7 h-7 text-gray-600" />
                </button>
              </div>

              {/* Right Column - Testimonial */}
                  <div className="space-y-8">
                    <p className="text-gray-700 dark:text-gray-300 text-xl leading-relaxed">
                  "{testimonials[currentSlide]?.testimonial || 'No testimonial available'}"
                </p>
                    <div className="space-y-4">
                      <h3 className="text-gray-900 dark:text-white font-bold text-xl">
                    {testimonials[currentSlide]?.name || 'Anonymous'}
                  </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-lg">
                    {testimonials[currentSlide]?.position || ''} {testimonials[currentSlide]?.company ? `, ${testimonials[currentSlide].company}` : ''}
                  </p>
                      <div className="flex space-x-2">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                            className={`w-6 h-6 ${i < (testimonials[currentSlide]?.rating || 5) ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'}`} 
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-600 dark:text-gray-400">No testimonials available</p>
            </div>
          )}
        </div>
      </section>

          {/* Modern Pricing Section */}
          <section className="py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-blue-900 dark:to-indigo-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100/20 via-purple-100/20 to-pink-100/20 dark:from-blue-800/10 dark:via-purple-800/10 dark:to-pink-800/10"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="max-w-8xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
              <div className="text-center mb-20">
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              {homeData?.pricing_title || 'Flexible Plans For Your Business Growth'}
            </h2>
                <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {homeData?.pricing_description || 'Choose the perfect plan that fits your business needs'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Left Column - Gradient Card with Toggle */}
                <div className="card-3d bg-gradient-to-br from-slate-800 via-purple-800 to-indigo-800 rounded-3xl p-10 text-white shadow-3d">
                  <h3 className="text-2xl font-bold mb-8">{homeData?.pricing_left_title || 'Choose Your Plan'}</h3>
                  <div className="flex items-center justify-center space-x-6 mb-10">
                    <span className={`text-lg font-semibold ${!pricingToggle ? 'text-white' : 'text-gray-300'}`}>
                  {homeData?.pricing_left_monthly_text || 'Present'}
                </span>
                <button 
                  onClick={() => setPricingToggle(!pricingToggle)}
                      className="relative w-20 h-10 bg-white/20 rounded-full transition-all duration-300 cursor-pointer border border-white/30"
                >
                      <div className={`absolute top-1 w-8 h-8 bg-white rounded-full transition-all duration-300 shadow-lg ${
                    pricingToggle ? 'right-1' : 'left-1'
                  }`}></div>
                </button>
                    <span className={`text-lg font-semibold ${pricingToggle ? 'text-white' : 'text-gray-300'}`}>
                  {homeData?.pricing_left_yearly_text || 'Future'}
                </span>
              </div>
                  <p className="text-base opacity-90 leading-relaxed">
                {homeData?.pricing_left_description || 'Compare present and future pricing to see the value of early adoption.'}
              </p>
            </div>

            {/* Pricing Plans */}
            {Array.isArray(homeData?.pricing_plans) && homeData.pricing_plans.length > 0 ? (
              homeData.pricing_plans.map((plan, index) => {
                const presentPrice = plan.monthly_price;
                const futurePrice = plan.yearly_price;
                const priceLabel = pricingToggle ? 'Future' : 'Present';
                
                // Calculate save percentage for present price
                const presentPriceNum = parseFloat(presentPrice);
                const futurePriceNum = parseFloat(futurePrice);
                const savePercentage = futurePriceNum > presentPriceNum ? 
                  Math.round(((futurePriceNum - presentPriceNum) / futurePriceNum) * 100) : 0;
                
                // Calculate increase percentage for future price
                const increasePercentage = presentPriceNum > 0 ? 
                  Math.round(((futurePriceNum - presentPriceNum) / presentPriceNum) * 100) : 0;
                
                return (
                      <div key={index} className="card-3d bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-3xl p-10 shadow-xl hover:scale-105 transition-all duration-500 border border-white/20 dark:border-slate-700/50">
                        <h3 className="text-gray-900 dark:text-white font-bold text-xl mb-6">{plan.name}</h3>
                        <div className="mb-8">
                          <div className="flex items-center gap-3">
                            {!pricingToggle ? (
                              // Present Price Display
                              <>
                                <span className="text-gray-900 dark:text-white font-bold text-3xl">{formatPrice(presentPrice)}</span>
                                {savePercentage > 0 && (
                                  <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full font-semibold">
                                    Save {savePercentage}%
                                  </span>
                                )}
                              </>
                            ) : (
                              // Future Price Display
                              <>
                                <span className="text-gray-900 dark:text-white font-bold text-3xl">{formatPrice(futurePrice)}</span>
                                {increasePercentage > 0 && (
                                  <span className="bg-orange-100 text-orange-800 text-sm px-3 py-1 rounded-full font-semibold">
                                    {increasePercentage}% Increase
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <ul className="space-y-4 mb-10">
                      {plan.features && plan.features.map((feature, featureIndex) => {
                        const IconComponent = getIconComponent(feature.icon || 'CheckCircle');
                        return (
                              <li key={featureIndex} className="flex items-center space-x-4">
                                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                  <IconComponent className="w-4 h-4 text-green-600" />
                                </div>
                                <span className="text-gray-700 dark:text-gray-300 text-base">{feature.text}</span>
                          </li>
                        );
                      })}
                    </ul>
                        <div className="space-y-4">
                      <Link 
                        to={homeData?.pricing_button_link || '/contact'}
                        className="w-full bg-gradient-to-r from-gray-900 to-black dark:from-white dark:to-gray-100 text-white dark:text-gray-900 py-4 px-6 rounded-xl font-bold text-base hover:scale-105 transition-all duration-300 shadow-lg block text-center"
                      >
                        {homeData?.pricing_button_text || 'Get Started'}
                      </Link>
                      
                      <button 
                        onClick={() => {
                          const message = `Hi! I'm interested in the ${plan.name} plan. Can you provide more details?`;
                          const whatsappUrl = `https://wa.me/919515377622?text=${encodeURIComponent(message)}`;
                          window.open(whatsappUrl, '_blank');
                        }}
                            className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-xl font-bold text-base hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg"
                      >
                        {(() => {
                          const IconComponent = getIconComponent(homeData?.pricing_whatsapp_button_icon || 'MessageCircle');
                              return <IconComponent className="w-5 h-5" />;
                        })()}
                        {homeData?.pricing_whatsapp_button_text || 'Order on WhatsApp'}
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              // Fallback to default plans if no data from admin
              [
                {
                  name: "Starter Plan",
                  monthly_price: "10000",
                  yearly_price: "15000",
                  features: [
                    { text: "5 Projects", icon: "CheckCircle" },
                    { text: "10GB Storage", icon: "CheckCircle" },
                    { text: "Basic Support", icon: "CheckCircle" },
                    { text: "Email Integration", icon: "CheckCircle" },
                    { text: "Analytics Dashboard", icon: "CheckCircle" },
                    { text: "Mobile App Access", icon: "CheckCircle" }
                  ]
                },
                {
                  name: "Professional Plan",
                  monthly_price: "25000",
                  yearly_price: "35000",
                  features: [
                    { text: "Unlimited Projects", icon: "CheckCircle" },
                    { text: "100GB Storage", icon: "CheckCircle" },
                    { text: "Priority Support", icon: "CheckCircle" },
                    { text: "Advanced Analytics", icon: "CheckCircle" },
                    { text: "Custom Domain", icon: "CheckCircle" },
                    { text: "API Access", icon: "CheckCircle" }
                  ]
                },
                {
                  name: "Premium Plan",
                  monthly_price: "50000",
                  yearly_price: "75000",
                  features: [
                    { text: "Everything in Professional", icon: "CheckCircle" },
                    { text: "White-label Solution", icon: "CheckCircle" },
                    { text: "Dedicated Support", icon: "CheckCircle" },
                    { text: "Custom Integrations", icon: "CheckCircle" },
                    { text: "Advanced Security", icon: "CheckCircle" },
                    { text: "SLA Guarantee", icon: "CheckCircle" }
                  ]
                }
              ].map((plan, index) => {
                const presentPrice = plan.monthly_price;
                const futurePrice = plan.yearly_price;
                
                // Calculate save percentage for present price
                const presentPriceNum = parseFloat(presentPrice);
                const futurePriceNum = parseFloat(futurePrice);
                const savePercentage = futurePriceNum > presentPriceNum ? 
                  Math.round(((futurePriceNum - presentPriceNum) / futurePriceNum) * 100) : 0;
                
                // Calculate increase percentage for future price
                const increasePercentage = presentPriceNum > 0 ? 
                  Math.round(((futurePriceNum - presentPriceNum) / presentPriceNum) * 100) : 0;
                
                return (
                      <div key={index} className="card-3d bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-3xl p-10 shadow-xl hover:scale-105 transition-all duration-500 border border-white/20 dark:border-slate-700/50">
                        <h3 className="text-gray-900 dark:text-white font-bold text-xl mb-6">{plan.name}</h3>
                        <div className="mb-8">
                          <div className="flex items-center gap-3">
                            {!pricingToggle ? (
                              // Present Price Display
                              <>
                                <span className="text-gray-900 dark:text-white font-bold text-3xl">{formatPrice(presentPrice)}</span>
                                {savePercentage > 0 && (
                                  <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full font-semibold">
                                    Save {savePercentage}%
                                  </span>
                                )}
                              </>
                            ) : (
                              // Future Price Display
                              <>
                                <span className="text-gray-900 dark:text-white font-bold text-3xl">{formatPrice(futurePrice)}</span>
                                {increasePercentage > 0 && (
                                  <span className="bg-orange-100 text-orange-800 text-sm px-3 py-1 rounded-full font-semibold">
                                    {increasePercentage}% Increase
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <ul className="space-y-4 mb-10">
                      {plan.features.map((feature, featureIndex) => {
                        const IconComponent = getIconComponent(feature.icon);
                        return (
                              <li key={featureIndex} className="flex items-center space-x-4">
                                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                  <IconComponent className="w-4 h-4 text-green-600" />
                                </div>
                                <span className="text-gray-700 dark:text-gray-300 text-base">{feature.text}</span>
                          </li>
                        );
                      })}
                    </ul>
                        <div className="space-y-4">
                      <Link 
                        to={homeData?.pricing_button_link || '/contact'}
                        className="w-full bg-gradient-to-r from-gray-900 to-black dark:from-white dark:to-gray-100 text-white dark:text-gray-900 py-4 px-6 rounded-xl font-bold text-base hover:scale-105 transition-all duration-300 shadow-lg block text-center"
                      >
                        {homeData?.pricing_button_text || 'Get Started'}
                      </Link>
                      
                      <button 
                        onClick={() => {
                          const message = `Hi! I'm interested in the ${plan.name} plan. Can you provide more details?`;
                          const whatsappUrl = `https://wa.me/919515377622?text=${encodeURIComponent(message)}`;
                          window.open(whatsappUrl, '_blank');
                        }}
                            className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-xl font-bold text-base hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg"
                      >
                        {(() => {
                          const IconComponent = getIconComponent(homeData?.pricing_whatsapp_button_icon || 'MessageCircle');
                              return <IconComponent className="w-5 h-5" />;
                        })()}
                        {homeData?.pricing_whatsapp_button_text || 'Order on WhatsApp'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>

          {/* Modern Newsletter and Support Section */}
          <section className="py-24 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-800/20 via-purple-800/30 to-slate-800/20"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Card - Newsletter */}
                <div className="card-3d bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-indigo-600/20 backdrop-blur-md rounded-3xl p-10 shadow-3d border border-white/20">
                  <h3 className="text-white font-bold text-2xl mb-6">
                {homeData?.newsletter_title || 'Subscribe to Our Newsletter'}
              </h3>
                  <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                {homeData?.newsletter_description || 'Stay updated with our latest news, offers, and industry insights delivered directly to your inbox.'}
              </p>
              <div className="space-y-4">
                {!newsletterSubscribed ? (
                  <form onSubmit={handleNewsletterSubscribe}>
                    <input
                      type="email"
                      placeholder="Enter your email address"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                          className="w-full px-6 py-4 border border-white/30 rounded-2xl bg-white/10 backdrop-blur-md text-white placeholder-gray-300 text-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                      required
                    />
                    <button 
                      type="submit"
                      disabled={newsletterLoading}
                          className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 px-6 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-4 shadow-lg"
                    >
                      {newsletterLoading ? 'Subscribing...' : (homeData?.newsletter_button_text || 'Subscribe Now')}
                    </button>
                    {newsletterMessage && (
                      <p className="mt-3 text-sm text-white/90">{newsletterMessage}</p>
                    )}
                  </form>
                ) : (
                  <div className="text-center">
                        <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                        <p className="text-green-400 font-bold text-xl">Successfully subscribed!</p>
                        <p className="text-gray-300 text-lg">Thank you for subscribing to our newsletter.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Card - Support 24/7 */}
                <div className="card-3d bg-gradient-to-br from-pink-600/20 via-purple-600/20 to-indigo-600/20 backdrop-blur-md rounded-3xl p-10 shadow-3d border border-white/20">
                  <div className="flex items-start space-x-8">
                <div className="flex-1">
                      <h3 className="text-white font-bold text-2xl mb-6">
                    {homeData?.support_title || 'Support 24/7'}
                  </h3>
                      <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                    {homeData?.support_description || 'Our dedicated support team is available around the clock to help you with any questions or issues.'}
                  </p>
                  <div className="space-y-3">
                  <a 
                    href={homeData?.support_button_link || 'mailto:support@company.com'} 
                          className="inline-flex items-center gap-3 bg-gradient-to-r from-gray-900 to-black dark:from-white dark:to-gray-100 text-white dark:text-gray-900 px-6 py-4 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-300 shadow-lg"
                  >
                      {(() => {
                        const IconComponent = getIconComponent(homeData?.support_icon || 'Headphones');
                            return <IconComponent className="w-5 h-5" />;
                      })()}
                    {homeData?.support_button_text || 'support@company.com'}
                  </a>
                    
                    {/* Extra Support Buttons */}
                    {(() => {
                      try {
                        const extraButtons = typeof homeData?.support_extra_buttons === 'string' 
                          ? JSON.parse(homeData.support_extra_buttons) 
                          : (homeData?.support_extra_buttons || []);
                        
                        if (Array.isArray(extraButtons) && extraButtons.length > 0) {
                          return (
                            <div className="flex flex-wrap gap-2">
                              {extraButtons.map((button, index) => {
                                const IconComponent = getIconComponent(button.icon || 'MessageCircle');
                                return (
                                  <a
                                    key={index}
                                    href={button.link || '#'}
                                        className="inline-flex items-center gap-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-3 rounded-xl font-semibold text-sm hover:scale-105 transition-all duration-300 shadow-lg border border-white/20"
                                  >
                                    <IconComponent className="w-4 h-4" />
                                    {button.text}
                                  </a>
                                );
                              })}
                            </div>
                          );
                        }
                        return null;
                      } catch (error) {
                        console.error('Error parsing support_extra_buttons:', error);
                        return null;
                      }
                    })()}
                  </div>
                </div>
                    <div className="w-28 h-28 bg-gradient-to-br from-pink-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                  {(() => {
                    const IconComponent = getIconComponent(homeData?.support_large_icon || 'Headphones');
                        return <IconComponent className="w-14 h-14 text-white" />;
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

          {/* Modern CTA Section */}
          <section className="py-24 bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 dark:from-blue-600 dark:via-purple-600 dark:to-indigo-600 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-100/20 via-purple-100/20 to-indigo-100/20 dark:from-blue-700/20 dark:via-purple-700/20 dark:to-indigo-700/20"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
            </div>
            
            <div className="container mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            {homeData?.more_title || 'Ready to Transform Your Business?'}
          </h2>
              <p className="text-xl text-gray-600 dark:text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed">
            {homeData?.more_description || 'Let\'s discuss your project and create something amazing together. Get a free consultation and quote today.'}
          </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Link to={homeData?.more_button1_link || '/contact'} className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-5 rounded-2xl font-bold text-lg hover:scale-105 transition-all duration-500 flex items-center justify-center gap-3 shadow-glow">
              {homeData?.more_button1_text || 'Start Your Project'}
                  <ArrowRight className="w-6 h-6" />
            </Link>
                <Link to={homeData?.more_button_link || '/quote'} className="card-3d border-2 border-gray-900 dark:border-white text-gray-900 dark:text-white px-10 py-5 rounded-2xl font-bold text-lg hover:bg-gray-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 hover:scale-105 transition-all duration-500 shadow-glow">
              {homeData?.more_button_text || 'Get Free Quote'}
            </Link>
          </div>
        </div>
      </section>
        </>
      )}
    </div>
  );
};

export default Home;
